import SlideFrame from "@/components/SlideFrame";
import { useState } from "react";
import {
  GraduationCap, LayoutDashboard, Sparkles, ExternalLink,
  Activity, Building2, PhoneCall, Brain, Newspaper, Users, Mic, ChevronDown,
} from "lucide-react";
import { isLegalPrimary, packFor } from "@/lib/industryPack";

/* ============================ 06 · Inside the Platform ============================ */
export function S_Platform({ idx, total, prospect }) {
  const pack = packFor(prospect);
  const lf = pack.lawFirm;
  const slug = (prospect?.slug || "").toLowerCase();
  const isSloan = slug === "sloan";
  const legalPrimary = isLegalPrimary(prospect);

  // Per-firm prototype dashboards for 2A law firms.
  const FIRM_PROTOTYPES = {
    "chapman-and-cutler": { src: "/brand/platform/lawfirm/chapman-dashboard.png",  caption: "Chapman and Cutler · prototype dashboard" },
    "dykema":             { src: "/brand/platform/lawfirm/dykema-dashboard.png",   caption: "Dykema · prototype dashboard" },
    "mavacy":             { src: "/brand/platform/lawfirm/mavacy-dashboard.png",   caption: "Mavacy · prototype dashboard" },
    "brom-law":           { src: "/brand/platform/lawfirm/brom-dashboard.png",     caption: "Brom Law · prototype dashboard" },
  };
  const firmProto = FIRM_PROTOTYPES[slug];

  const ALL_TABS = {
    dashboard: {
      id: "dashboard", icon: LayoutDashboard, label: "Compliance Cockpit",
      title: "Your whole program on one screen.",
      blurb: "Overdue tasks, your compliance calendar, your AI agents, your audit findings — built so a team of one can run a full program.",
      img: "/brand/platform/dashboard.jpg", alt: "Compliance dashboard",
    },
    monitoring: {
      id: "monitoring", icon: Activity, label: "Monitoring & Testing",
      title: "Continuous testing — not a once-a-year scramble.",
      blurb: "Schedule control tests, sample transactions, run periodic reviews. Findings flow straight into the evidence file. Examiners ask for proof — you click Export.",
      img: "/brand/platform/monitoring.jpg", alt: "Monitoring & testing console",
    },
    vendor: {
      id: "vendor", icon: Building2, label: "Vendor Management",
      title: "Ongoing third-party risk, automated.",
      blurb: "Onboarding form, OFAC + PEP + adverse news, principal background checks for ≥25% owners, tier scoring, ongoing monitoring. Continuous third-party oversight is both a regulatory requirement and a basic best practice — handled.",
      img: "/brand/platform/vendor.jpg", alt: "Vendor management workflow",
    },
    training: {
      id: "training", icon: GraduationCap, label: "Training & Quizzes",
      title: pack.trainingTitle || "Role-based modules with evidence of completion.",
      blurb: pack.trainingBlurb || "BSA/AML, UDAAP, ECOA, FCRA, privacy, fair lending. Video lessons + quizzes. Audit-ready evidence per employee.",
      img: "/brand/platform/training.jpg", alt: "Compliance training modules",
    },
    hotline: {
      id: "hotline", icon: PhoneCall, label: "Compliance Hotline",
      title: "White-labeled 24/7 reporting + triage.",
      blurb: "A revenue-generating service for your clients (or an internal hotline for your firm). Anonymous reporting, intake triage, case management, evidence trail.",
      img: "/brand/platform/dashboard.jpg", alt: "Compliance hotline intake",
    },
    prototype: {
      id: "prototype", icon: Sparkles, label: "Your Prototype",
      title: "We hand you a working environment.",
      blurb: "Branded prototype with your policies, your calendar, your team — built during the 45-day rollout so you can walk it and refine it before you sign off.",
      img: "/brand/platform/prototype.jpg", alt: "Prototype dashboard for client",
    },
    // ----- Law-firm modules -----
    "attorney-dashboard": {
      id: "attorney-dashboard", icon: LayoutDashboard, label: "Attorney View — Matters & Dashboard",
      title: "Every matter, every deadline, every client message — one screen.",
      blurb: "Surface what needs attention, surface what's at risk. Matters list, CompliMind summaries, and below the fold — live team capacity so partners can balance the book in real time.",
      img: "/brand/platform/attorney-dashboard.png",
      imgSecondary: "/brand/platform/attorney-team-capacity.png",
      imgSecondaryLabel: "Scroll · team capacity",
      caption: "Attorney view · matters + CompliMind on top; team capacity directly below.",
    },
    complimind: {
      id: "complimind", icon: Brain, label: "CompliMind — Your AI Legal Assistant Team",
      title: "Summarizes any matter. Surfaces firm precedent. Drafts in your voice.",
      blurb: "Trained on your firm's templates and prior work — not the open internet. Privilege-aware: every AI output is taggable as Attorney-Client Privileged or Attorney Work Product.",
      img: "/brand/platform/lawfirm/complimind.png",
      alt: "CompliMind — firm-trained AI assistant team",
      caption: "CompliMind · firm-trained AI assistants with privilege-aware outputs.",
    },
    "client-news": {
      id: "client-news", icon: Newspaper, label: "Client News & Intelligence",
      title: "Walk into every call already up to speed.",
      blurb: "Proactive monitoring of every client. Negative news alerts. M&A signals. Regulatory developments in their industry. Surfaces what matters before they mention it.",
      img: "/brand/platform/lawfirm/client-news.png",
      alt: "Client news & intelligence feed",
      caption: "Client intelligence feed · monitored across the entire book.",
    },
    "client-portal": {
      id: "client-portal", icon: Users, label: "Client Portal",
      title: "The kind of transparency that gets you referred.",
      blurb: "What your client sees — and you control every byte of it. Real-time chat with their attorney team. Matter status. Documents. Webinar library. Curated research you've cleared for consumption.",
      img: "/brand/platform/client-portal-dash.png",
      imgSecondary: "/brand/platform/client-portal-cal.png",
      imgSecondaryLabel: "Scroll · matter calendar",
      caption: "Client portal · branded as your firm · you choose what each client sees.",
    },
    notetaker: {
      id: "notetaker", icon: Mic, label: "Intuitive Design and Built-in Protections",
      title: "Govern which meetings AI can join.",
      blurb: "Auto-flag transcripts as privileged or work product. Audit trail for every AI interaction with client matter. The privilege landmine — defused.",
      video: "/brand/video/lawfirm-tool.mp4",
      caption: "Quick look at the firm dashboard — and a reminder: this is a foundation, not a finished product. We build whatever makes sense for your firm and your clients on top of our protected, privilege-aware enterprise technology.",
    },
    "law-prototype": {
      id: "law-prototype", icon: Sparkles, label: "Your Prototype",
      title: firmProto ? `Built for ${prospect?.company_name || "your firm"} in 45 days.` : "Built for your firm in 45 days.",
      blurb: "Configured to your practice areas, your matter types, your client base. Walk it with our team before you sign off.",
      ...(firmProto
        ? { img: firmProto.src, alt: firmProto.caption, caption: firmProto.caption }
        : { wireframe: "prototype", caption: "Your prototype · your branding, your matter taxonomy." }),
    },
  };

  const tabIds = lf
    ? (pack.lawFirmPlatformTabs || ["attorney-dashboard", "complimind", "client-news", "client-portal", "notetaker", "law-prototype"])
    : [...(pack.platformTabs || ["dashboard", "monitoring", "vendor", "training"]), "prototype"];
  const tabs = tabIds.map((id) => ALL_TABS[id]).filter(Boolean);
  const [active, setActive] = useState(tabs[0].id);
  const cur = tabs.find((t) => t.id === active) || tabs[0];
  const Icon = cur.icon;

  return (
    <SlideFrame idx={idx} total={total} kicker="The Platform" dark={false}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">
            {legalPrimary ? "/ and · the tech layer for your in-house team" : "/ inside guardian"}
          </p>
          <h2 className="serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
            {legalPrimary ? (
              <>Tools your in-house team can <em className="italic text-sun-600">actually use.</em></>
            ) : (
              <>Built so anyone on your team can <em className="italic text-sun-600">actually use it.</em></>
            )}
          </h2>
          {legalPrimary && (
            <p className="mt-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55" data-testid="platform-secondary-flag">
              Secondary to our outside-counsel engagement. Bundle it in, or pass entirely.
            </p>
          )}
          <p className="mt-4 text-ink-900/70 text-base">
            {legalPrimary
              ? "Beyond CompliSun Legal as your outside counsel, your in-house GC + compliance team can run on the same platform we use day-to-day — live matter status, AI that remembers everything we've advised on, vendor management, and audit-ready evidence captured as we go."
              : (pack.platformBody || "Not a binder full of policies. A live system — AI agents, training, phishing tests, vendor management, calendars, audit prep — all in one place, all auditor-ready.")}
          </p>

          {lf ? (
            <div className="mt-6 border border-sun-500/40 rounded-md bg-sun-500/8 p-4" data-testid="platform-lawfirm-sandbox">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">For your firm</div>
              <div className="serif text-base text-ink-900 leading-snug mt-1">
                {pack.platformFooter}
              </div>
            </div>
          ) : (
            <div className="mt-6 border border-ink-900/12 rounded-md bg-white/80 p-4">
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">Try the free playground</div>
              <div className="serif text-xl text-ink-900 leading-tight mt-1">
                60 days free at <span className="text-sun-700">complisunguardian.com</span>
              </div>
              <p className="mt-2 text-xs text-ink-900/65 leading-relaxed">
                The free version is a playground to feel out the AI and the automation.
                Your <strong className="text-ink-900">enterprise prototype</strong> is built
                separately — fully protected, configured to your business, and unique to you.
              </p>
              <a href="https://complisunguardian.com" target="_blank" rel="noreferrer"
                 data-testid="platform-cta"
                 className="mt-3 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900 hover:text-sun-700 transition-colors border border-ink-900/15 rounded px-3 py-2">
                Start your trial <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          <div className="mt-5 flex flex-col gap-1.5">
            {tabs.map((t) => {
              const TIcon = t.icon;
              const on = t.id === active;
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  data-testid={`platform-tab-${t.id}`}
                  className={`flex items-center gap-3 text-left px-3 py-2 rounded-md border transition-all
                    ${on
                      ? "border-sun-500 bg-sun-500/15 shadow-sm ring-2 ring-sun-500/40 translate-x-0.5"
                      : "border-ink-900/10 bg-white/40 hover:border-ink-900/25"}`}
                >
                  <TIcon className={`h-4 w-4 ${on ? "text-sun-700" : "text-ink-900/55"}`} strokeWidth={1.5} />
                  <span className={`serif text-sm ${on ? "text-ink-900 font-medium" : "text-ink-900/70"}`}>{t.label}</span>
                  {on && <span className="ml-auto mono text-[9px] uppercase tracking-[0.18em] text-sun-700">active</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview pane */}
        <div className="col-span-12 md:col-span-7" key={active}>
          <div className="border border-ink-900/12 rounded-md overflow-hidden bg-white shadow-sm animate-fade-in">
            <div className="bg-cream-100 grid place-items-center" style={{ aspectRatio: "16 / 10" }}>
              {cur.video ? (
                <video
                  src={cur.video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain bg-black"
                  data-testid={`platform-video-${cur.id}`}
                />
              ) : cur.wireframe ? (
                <LawFirmWireframe variant={cur.wireframe} />
              ) : cur.imgSecondary ? (
                <DualImageScroll primary={cur.img} secondary={cur.imgSecondary} secondaryLabel={cur.imgSecondaryLabel} alt={cur.alt || cur.label} />
              ) : (
                <img src={cur.img} alt={cur.alt}
                     className="w-full h-full object-contain"
                     data-testid={`platform-screenshot-${cur.id}`} />
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">{cur.label}</div>
              </div>
              <div className="serif text-xl text-ink-900 mt-2 leading-tight">{cur.title}</div>
              <div className="text-sm text-ink-900/70 mt-2 leading-relaxed">{cur.blurb}</div>
              {cur.caption && (
                <div className="mt-3 mono text-[9px] uppercase tracking-[0.22em] text-ink-900/55 border-t border-ink-900/8 pt-3">
                  {cur.caption}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

/* ============== Wireframe-style illustration for law-firm modules ============== */
function LawFirmWireframe({ variant }) {
  const baseRect = (x, y, w, h, color = "#E8E1CC", key) =>
    <rect key={key} x={x} y={y} width={w} height={h} rx="3" fill={color} />;
  const dot = (x, y, color = "#F5B400", key) =>
    <circle key={key} cx={x} cy={y} r="3" fill={color} />;

  // SVG layouts per variant — kept simple, no real client data, color-true to the cream/sun palette
  const variants = {
    attorney: (
      <>
        {baseRect(20, 20, 160, 14, "#0A0A0A")}
        {/* Left col — matters list */}
        {baseRect(20, 50, 160, 200, "#FBF4DC")}
        {[60, 92, 124, 156, 188, 220].map((y) => (
          <g key={y}>
            {baseRect(28, y, 144, 20, "#FFFFFF")}
            {dot(38, y + 10, "#F5B400")}
            {baseRect(50, y + 5, 100, 4, "#0A0A0A")}
            {baseRect(50, y + 12, 60, 3, "#D9D0B0")}
          </g>
        ))}
        {/* Middle — CompliMind summary */}
        {baseRect(200, 50, 200, 200, "#FFFFFF")}
        {baseRect(210, 60, 180, 8, "#F5B400")}
        {[80, 95, 110, 125, 140, 155, 180, 195, 210].map((y) => baseRect(210, y, 180, 4, "#E8E1CC", `cm-${y}`))}
        {baseRect(210, 220, 60, 18, "#0A0A0A")}
        {/* Right — client news feed */}
        {baseRect(420, 50, 140, 200, "#FBF4DC")}
        {[60, 110, 160, 210].map((y) => (
          <g key={y}>
            {baseRect(428, y, 124, 38, "#FFFFFF")}
            {baseRect(434, y + 6, 90, 4, "#0A0A0A")}
            {baseRect(434, y + 14, 110, 3, "#D9D0B0")}
            {baseRect(434, y + 21, 70, 3, "#D9D0B0")}
          </g>
        ))}
      </>
    ),
    complimind: (
      <>
        {baseRect(20, 20, 540, 14, "#0A0A0A")}
        {baseRect(40, 60, 500, 32, "#FBF4DC")}
        {baseRect(48, 70, 200, 4, "#F5B400")}
        {baseRect(48, 80, 480, 4, "#0A0A0A")}
        {/* Chat thread */}
        {[110, 150, 200, 250].map((y, i) => (
          <g key={y}>
            {baseRect(40, y, 200, 30, i % 2 ? "#FFFFFF" : "#FBF4DC")}
            {baseRect(48, y + 8, 140, 4, "#0A0A0A")}
            {baseRect(48, y + 18, 80, 3, "#D9D0B0")}
          </g>
        ))}
        {/* Privilege flags */}
        {baseRect(360, 110, 200, 100, "#FFFFFF")}
        {baseRect(370, 120, 80, 4, "#F5B400")}
        {[140, 156, 172, 188].map((y) => baseRect(370, y, 180, 3, "#D9D0B0", `cm2-${y}`))}
        {baseRect(370, 200, 60, 14, "#0A0A0A")}
      </>
    ),
    news: (
      <>
        {baseRect(20, 20, 540, 14, "#0A0A0A")}
        {[50, 130, 210].map((y) => (
          <g key={y}>
            {baseRect(20, y, 540, 70, "#FBF4DC")}
            {baseRect(30, y + 10, 50, 50, "#F5B400")}
            {baseRect(90, y + 10, 200, 8, "#0A0A0A")}
            {baseRect(90, y + 22, 460, 4, "#D9D0B0")}
            {baseRect(90, y + 32, 460, 4, "#D9D0B0")}
            {baseRect(90, y + 42, 300, 4, "#D9D0B0")}
            {baseRect(490, y + 50, 60, 14, "#FF801D")}
          </g>
        ))}
      </>
    ),
    portal: (
      <>
        {baseRect(20, 20, 540, 14, "#F5B400")}
        {/* Left — matters / status */}
        {baseRect(20, 50, 220, 200, "#FBF4DC")}
        {[60, 95, 130, 165, 200].map((y) => (
          <g key={y}>
            {baseRect(28, y, 204, 25, "#FFFFFF")}
            {baseRect(36, y + 7, 100, 4, "#0A0A0A")}
            {baseRect(36, y + 15, 140, 3, "#D9D0B0")}
            {dot(220, y + 12, "#F5B400")}
          </g>
        ))}
        {/* Right — chat with attorney */}
        {baseRect(260, 50, 300, 200, "#FFFFFF")}
        {baseRect(270, 60, 120, 6, "#F5B400")}
        {[80, 110, 150, 190].map((y, i) => (
          <g key={y}>
            {baseRect(i % 2 ? 380 : 270, y, 160, 22, i % 2 ? "#0A0A0A" : "#FBF4DC")}
            {baseRect(i % 2 ? 388 : 278, y + 8, 120, 3, i % 2 ? "#FDFBF5" : "#0A0A0A")}
          </g>
        ))}
        {baseRect(270, 220, 280, 20, "#FBF4DC")}
      </>
    ),
    notetaker: (
      <>
        {baseRect(20, 20, 540, 14, "#0A0A0A")}
        {baseRect(40, 60, 500, 30, "#FBF4DC")}
        {baseRect(48, 70, 80, 10, "#F5B400")}
        {baseRect(440, 70, 90, 10, "#0A0A0A")}
        {/* Meeting list */}
        {[110, 145, 180, 215].map((y, i) => (
          <g key={y}>
            {baseRect(40, y, 500, 28, "#FFFFFF")}
            {dot(54, y + 14, i === 0 ? "#FF801D" : "#F5B400")}
            {baseRect(70, y + 8, 200, 4, "#0A0A0A")}
            {baseRect(70, y + 17, 150, 3, "#D9D0B0")}
            {baseRect(360, y + 8, 80, 14, i % 2 ? "#FF801D" : "#F5B400")}
            {baseRect(450, y + 8, 80, 14, "#0A0A0A")}
          </g>
        ))}
      </>
    ),
    prototype: (
      <>
        {baseRect(20, 20, 540, 14, "#0A0A0A")}
        {baseRect(40, 60, 240, 180, "#FBF4DC")}
        {baseRect(50, 70, 100, 8, "#F5B400")}
        {[90, 105, 120, 135, 150, 175, 190, 205].map((y) => baseRect(50, y, 220, 4, "#D9D0B0", `pp-${y}`))}
        {baseRect(50, 220, 80, 14, "#0A0A0A")}
        {baseRect(300, 60, 240, 180, "#FFFFFF")}
        {baseRect(310, 70, 80, 80, "#F5B400")}
        {baseRect(310, 160, 220, 6, "#0A0A0A")}
        {[175, 188, 201, 214].map((y) => baseRect(310, y, 220, 4, "#D9D0B0", `pp2-${y}`))}
        {baseRect(310, 228, 60, 12, "#FF801D")}
      </>
    ),
  };

  return (
    <svg viewBox="0 0 580 260" className="w-full h-full" preserveAspectRatio="xMidYMid meet"
         data-testid="platform-wireframe">
      <rect width="580" height="260" fill="#FFFFFF" />
      {variants[variant] || variants.attorney}
    </svg>
  );
}


/* Dual-image preview with a tappable toggle to swap between "above the fold" and "below the fold". */
function DualImageScroll({ primary, secondary, secondaryLabel, alt }) {
  const [view, setView] = useState("primary");
  const src = view === "primary" ? primary : secondary;
  return (
    <div className="relative w-full h-full">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-contain transition-opacity duration-200"
        data-testid={`platform-screenshot-${view}`}
        key={src}
      />
      <button
        type="button"
        onClick={() => setView(view === "primary" ? "secondary" : "primary")}
        data-testid="platform-screenshot-toggle"
        className="absolute bottom-3 right-3 mono text-[10px] uppercase tracking-[0.18em] bg-ink-900/85 hover:bg-ink-900 text-cream rounded-md px-3 py-1.5 flex items-center gap-1.5 backdrop-blur-sm transition-colors"
      >
        <ChevronDown
          className={`h-3 w-3 transition-transform ${view === "primary" ? "" : "rotate-180"}`}
          strokeWidth={2}
        />
        {view === "primary" ? (secondaryLabel || "Scroll for more") : "Back to top"}
      </button>
    </div>
  );
}
